import { useEffect, useRef, useState } from "react";
import { MIN_PASSWORD_LENGTH, useT } from "@reciperoll/core";
import { supabase } from "../lib/supabase";
import { useAuth, signOut } from "../lib/auth";
import { AuthLogo } from "./AuthLogo";
import { readAuthError } from "./AuthCallback";

// Shown after a password-reset link is opened (recovery session active). Sets a
// new password, then ends recovery so AuthGate swaps in the app (the recovery
// session is a normal authenticated session once the password is updated).
export function ResetPassword() {
  const t = useT();
  const { session, loading, endRecovery } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An expired/already-used link surfaces an error in the URL rather than a session.
  const [linkError, setLinkError] = useState<string | null>(readAuthError);

  const goHome = () => window.location.replace("/");

  // Establish the recovery session from the link. Preferred path is the
  // token_hash OTP (not device-bound, works cross-device); legacy PKCE `?code=`
  // links still in inboxes are handled by detectSessionInUrl automatically.
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    const params = new URLSearchParams(window.location.search);
    const token_hash = params.get("token_hash");
    const type = params.get("type");
    if (token_hash && type) {
      handled.current = true;
      supabase.auth
        .verifyOtp({ token_hash, type: type as never })
        .then(({ error }) => error && setLinkError(error.message));
    }
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("account.passwordMinLength", { count: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t("account.passwordsDontMatch"));
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      setError(error.message);
      return;
    }
    endRecovery();
    // Full nav to "/" strips the ?code from the URL and re-evaluates AuthGate,
    // which now shows the app (the recovery session is a normal session).
    goHome();
  }

  // The recovery link's PKCE code is exchanged by detectSessionInUrl on load, so
  // the session can lag a moment behind mount. Gate the form until it lands (or
  // surface an expired/invalid-link error) so an update can't fire session-less.
  if (linkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper p-4">
        <div className="w-full max-w-sm rounded-2xl bg-surface p-8 text-center shadow-sm ring-1 ring-line">
          <AuthLogo />
          <h1 className="mt-4 text-xl font-bold text-ink">{t("auth.linkDidntWork")}</h1>
          <p className="mt-2 text-sm text-ink-soft">{linkError}</p>
          <p className="mt-2 text-sm text-ink-soft">
            {t("auth.linkExpired")}
          </p>
          <button
            onClick={goHome}
            className="mt-6 w-full rounded-lg bg-terracotta px-4 py-2.5 font-semibold text-white transition hover:bg-terracotta-dark"
          >
            {t("auth.backToSignIn")}
          </button>
        </div>
      </div>
    );
  }

  if (loading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper p-4">
        <div className="w-full max-w-sm rounded-2xl bg-surface p-8 text-center shadow-sm ring-1 ring-line">
          <AuthLogo />
          <p className="mt-4 text-sm text-ink-soft">{t("auth.verifyingResetLink")}</p>
          <button
            onClick={goHome}
            className="mt-6 w-full text-center text-sm text-ink-soft hover:text-ink"
          >
            {t("auth.backToSignIn")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-sm ring-1 ring-line"
      >
        <AuthLogo />
        <h1 className="mt-4 text-2xl font-bold text-ink">{t("auth.setNewPassword")}</h1>
        <p className="mt-1 text-sm text-ink-soft">{t("auth.chooseNewPassword")}</p>

        <label className="mt-6 block text-sm font-medium text-ink">
          {t("account.newPassword")}
          <input
            type="password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
          />
        </label>

        <label className="mt-4 block text-sm font-medium text-ink">
          {t("auth.confirmPassword")}
          <input
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-ink outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
          />
        </label>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-lg bg-terracotta px-4 py-2.5 font-semibold text-white transition hover:bg-terracotta-dark disabled:opacity-60"
        >
          {busy ? t("auth.updating") : t("account.updatePassword")}
        </button>

        <button
          type="button"
          onClick={async () => {
            endRecovery();
            await signOut();
            // Leave /reset-password so AuthGate stops matching the recovery path.
            goHome();
          }}
          className="mt-4 w-full text-center text-sm text-ink-soft hover:text-ink"
        >
          {t("common.cancel")}
        </button>
      </form>
    </div>
  );
}
